import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { AdminEquipmentService } from '@app/api/api';
import { environment } from 'environments/environment';

declare var Chart: any;

@Component({
  selector: 'meu-admin-equipment-statistics',
  templateUrl: './admin-equipment-statistics.component.html',
  styleUrls: ['./admin-equipment-statistics.component.scss'],
})
export class AdminEquipmentStatisticsComponent implements OnInit, AfterViewInit {
  @ViewChild('statusChart', { static: false }) statusChartRef!: ElementRef;
  @ViewChild('brandChart', { static: false }) brandChartRef!: ElementRef;
  @ViewChild('modelChart', { static: false }) modelChartRef!: ElementRef;
  @ViewChild('topProductsChart', { static: false }) topProductsChartRef!: ElementRef;

  host: string = '';
  loading = false;
  loadingTopProducts = false;

  // Filters
  dateSlected: any = {};
  statusFilter: string = '';
  topLimit: number = 10;
  topLimitOptions: number[] = [5, 10, 20, 50];

  get fromDate(): string | undefined {
    if (this.dateSlected && this.dateSlected.startDate && moment(this.dateSlected.startDate).isValid()) {
      return moment(this.dateSlected.startDate).format('YYYY-MM-DD');
    }
    return undefined;
  }

  get toDate(): string | undefined {
    if (this.dateSlected && this.dateSlected.endDate && moment(this.dateSlected.endDate).isValid()) {
      return moment(this.dateSlected.endDate).format('YYYY-MM-DD');
    }
    return undefined;
  }

  // Data
  summary: any = null;
  byBrand: any[] = [];
  byModel: any[] = [];
  byUser: any[] = [];
  topProducts: any[] = [];

  loadingRepairCost = false;
  repairCostSummary: any = null;
  repairCostRanked: any[] = [];
  expandedRepairEquipmentId: string | null = null;
  loadingRepairCostDetail = false;
  repairCostDetail: any[] = [];

  // Charts
  private statusChart: any = null;
  private brandChart: any = null;
  private modelChart: any = null;
  private topProductsChart: any = null;

  private readonly statusColors: Record<string, string> = {
    active: '#36a3eb',
    maintenance: '#ffb822',
    inactive: '#7b7f9e',
    retired: '#f4516c',
  };
  private readonly palette: string[] = [
    '#36a3eb',
    '#ffb822',
    '#0abb87',
    '#f4516c',
    '#8950FC',
    '#1bc5bd',
    '#fd7e14',
    '#20c997',
    '#6f42c1',
    '#e83e8c',
    '#17a2b8',
    '#ffc107',
  ];

  constructor(
    private adminEquipmentService: AdminEquipmentService,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit() {
    this.host = environment.backendhost;
  }

  ngAfterViewInit() {
    this.loadStatistics();
    this.loadTopProducts();
    this.loadRepairCostReport();
  }

  @HostListener('window:resize')
  onResize() {
    if (this.statusChart || this.brandChart || this.modelChart) {
      setTimeout(() => this.renderCharts(), 100);
    }
    if (this.topProductsChart) {
      setTimeout(() => this.renderTopProductsChart(), 100);
    }
  }

  loadStatistics() {
    this.loading = true;
    this.adminEquipmentService
      .apiAdminMyEquipmentStatisticsGet(
        undefined,
        undefined,
        undefined,
        this.statusFilter || undefined,
        this.fromDate || undefined,
        this.toDate || undefined,
        this.topLimit
      )
      .subscribe(
        (res) => {
          this.loading = false;
          if (res && res.success && res.data) {
            this.summary = res.data.summary || null;
            this.byBrand = res.data.by_brand || [];
            this.byModel = res.data.by_model || [];
            this.byUser = res.data.by_user || [];
            this.ref.detectChanges();
            setTimeout(() => {
              this.renderCharts();
              this.renderTopProductsChart();
            }, 0);
          } else if (res && res.data === null) {
            // API returned error
            this.toastr.error(res.message || 'Lỗi khi tải thống kê thiết bị');
          } else if (res && res.data) {
            // Some backends return data without `success` flag
            this.summary = res.data.summary || null;
            this.byBrand = res.data.by_brand || [];
            this.byModel = res.data.by_model || [];
            this.byUser = res.data.by_user || [];
            this.ref.detectChanges();
            setTimeout(() => {
              this.renderCharts();
              this.renderTopProductsChart();
            }, 0);
          } else {
            this.toastr.error((res && res.message) || 'Lỗi khi tải thống kê thiết bị');
          }
        },
        (err) => {
          this.loading = false;
          this.toastr.error('Lỗi khi tải thống kê thiết bị');
        }
      );
  }

  private renderCharts() {
    this.renderStatusChart();
    this.renderBrandChart();
    // Temporarily hidden per client request (duplicates the "Top dòng model" table) — see renderModelChart() below
    // this.renderModelChart();
  }

  private renderStatusChart() {
    if (!this.statusChartRef) {
      return;
    }
    const data = (this.summary && this.summary.by_status) || [];
    const labels = data.map((x: any) => this.getStatusLabel(x.status));
    const values = data.map((x: any) => x.count);
    const colors = data.map((x: any) => this.statusColors[x.status] || '#999');

    if (this.statusChart) {
      this.statusChart.destroy();
    }
    this.statusChart = new Chart(this.statusChartRef.nativeElement, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{ data: values, backgroundColor: colors }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 60,
        legend: { position: 'bottom' },
        tooltips: {
          callbacks: {
            label: (tooltipItem: any, chartData: any) => {
              const value = chartData.datasets[0].data[tooltipItem.index];
              const total = chartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
              const percent = total ? ((value / total) * 100).toFixed(1) : '0';
              return `${chartData.labels[tooltipItem.index]}: ${value} (${percent}%)`;
            },
          },
        },
      },
    });
  }

  private aggregateTopOthers(data: any[], labelKey: string, topN: number = 4): any[] {
    if (!data || data.length <= topN) {
      return data ? data.slice() : [];
    }
    const top = data.slice(0, topN);
    const others = data.slice(topN);
    const othersCount = others.reduce((a: number, b: any) => a + (b.count || 0), 0);
    const othersPercentage = others.reduce((a: number, b: any) => a + (b.percentage || 0), 0);
    const othersItem: any = {
      [labelKey]: 'Khác',
      count: othersCount,
      percentage: othersPercentage,
      _isOthers: true,
    };
    if (labelKey === 'model_name') {
      othersItem.brand_name = '';
    }
    top.push(othersItem);
    return top;
  }

  private renderBrandChart() {
    if (!this.brandChartRef) {
      return;
    }
    const data = this.aggregateTopOthers(this.byBrand, 'brand_name', 4);
    const labels = data.map((x: any) => x.brand_name || 'Khác');
    const values = data.map((x: any) => x.count);
    const colors = data.map((_: any, i: number) =>
      i < 4 ? this.palette[i] : '#7b7f9e'
    );

    if (this.brandChart) {
      this.brandChart.destroy();
    }
    this.brandChart = new Chart(this.brandChartRef.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Số lượng',
            data: values,
            backgroundColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem: any, chartData: any) => {
              const value = chartData.datasets[0].data[tooltipItem.index];
              const percent = data[tooltipItem.index] ? data[tooltipItem.index].percentage : 0;
              return `${chartData.labels[tooltipItem.index]}: ${value} (${percent}%)`;
            },
          },
        },
      },
    });
  }

  // Temporarily hidden per client request (the "Top dòng model" chart duplicates the table of the same name below).
  // Kept intact in case the client changes their mind — just uncomment this and the call
  // in renderCharts(), plus the #modelChart HTML block.
  // private renderModelChart() {
  //   if (!this.modelChartRef) {
  //     return;
  //   }
  //   const aggregated = this.aggregateTopOthers(this.byModel, 'model_name', 4);
  //   const data = aggregated.slice().reverse(); // reverse for horizontal bar top-down
  //   const labels = data.map((x: any) => x.model_name || 'Khác');
  //   const values = data.map((x: any) => x.count);
  //   const colors = data.map((x: any) =>
  //     x._isOthers ? '#7b7f9e' : this.palette[aggregated.indexOf(x)]
  //   );
  //
  //   if (this.modelChart) {
  //     this.modelChart.destroy();
  //   }
  //   this.modelChart = new Chart(this.modelChartRef.nativeElement, {
  //     type: 'horizontalBar',
  //     data: {
  //       labels,
  //       datasets: [
  //         {
  //           label: 'Số lượng',
  //           data: values,
  //           backgroundColor: colors,
  //         },
  //       ],
  //     },
  //     options: {
  //       responsive: true,
  //       maintainAspectRatio: false,
  //       legend: { display: false },
  //       scales: {
  //         xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
  //       },
  //       tooltips: {
  //         callbacks: {
  //           label: (tooltipItem: any, chartData: any) => {
  //             const idx = tooltipItem.index;
  //             const value = chartData.datasets[0].data[idx];
  //             const percent = data[idx] ? data[idx].percentage : 0;
  //             const brand = data[idx] ? data[idx].brand_name : '';
  //             return `${value} thiết bị (${percent}%)${brand ? ' - ' + brand : ''}`;
  //           },
  //         },
  //       },
  //     },
  //   });
  // }

  applyFilter() {
    this.loadStatistics();
    this.loadTopProducts();
    this.loadRepairCostReport();
  }

  onDateRangeChange() {
    this.loadStatistics();
    this.loadTopProducts();
    this.loadRepairCostReport();
  }

  resetFilter() {
    this.dateSlected = { startDate: null, endDate: null };
    this.statusFilter = '';
    this.topLimit = 10;
    this.loadStatistics();
    this.loadTopProducts();
    this.loadRepairCostReport();
  }


  loadRepairCostReport(): void {
    this.loadingRepairCost = true;
    this.expandedRepairEquipmentId = null;
    this.repairCostDetail = [];
    this.adminEquipmentService
      .apiAdminMyEquipmentRepairCostReportGet(
        undefined,
        undefined,
        undefined,
        this.fromDate || undefined,
        this.toDate || undefined,
        this.topLimit
      )
      .subscribe(
        (res) => {
          this.loadingRepairCost = false;
          if (res && res.success && res.data) {
            this.repairCostSummary = res.data.summary || null;
            this.repairCostRanked = res.data.ranked_equipments || [];
          } else {
            this.repairCostSummary = null;
            this.repairCostRanked = [];
          }
          this.ref.detectChanges();
        },
        () => {
          this.loadingRepairCost = false;
          this.repairCostSummary = null;
          this.repairCostRanked = [];
          this.toastr.error('Lỗi khi tải báo cáo chi phí sửa chữa');
        }
      );
  }

  toggleRepairCostDetail(equipment: any): void {
    const id = equipment && equipment.my_equipment_id;
    if (!id) {
      return;
    }
    if (this.expandedRepairEquipmentId === id) {
      this.expandedRepairEquipmentId = null;
      this.repairCostDetail = [];
      return;
    }
    this.expandedRepairEquipmentId = id;
    this.repairCostDetail = [];
    this.loadingRepairCostDetail = true;
    this.adminEquipmentService
      .apiAdminMyEquipmentRepairCostDetailGet(id, this.fromDate || undefined, this.toDate || undefined)
      .subscribe(
        (res) => {
          this.loadingRepairCostDetail = false;
          this.repairCostDetail = (res && res.success && res.data) || [];
          this.ref.detectChanges();
        },
        () => {
          this.loadingRepairCostDetail = false;
          this.repairCostDetail = [];
        }
      );
  }

  loadTopProducts() {
    this.loadingTopProducts = true;
    this.adminEquipmentService
      .apiAdminMyEquipmentTopPurchasedProductsGet(
        undefined,
        undefined,
        this.fromDate || undefined,
        this.toDate || undefined,
        this.topLimit
      )
      .subscribe(
        (res) => {
          this.loadingTopProducts = false;
          this.topProducts = (res && res.success && res.data) || [];
          this.ref.detectChanges();
          setTimeout(() => this.renderTopProductsChart(), 0);
        },
        () => {
          this.loadingTopProducts = false;
          this.topProducts = [];
          this.toastr.error('Lỗi khi tải top sản phẩm mua nhiều nhất');
        }
      );
  }

  private truncateLabel(name: string, maxLength: number = 20): string {
    if (!name) return '';
    return name.length > maxLength ? name.slice(0, maxLength).trim() + '...' : name;
  }

  private renderTopProductsChart() {
    if (!this.topProductsChartRef) {
      return;
    }
    // Keep API's descending order so the highest revenue product renders at the top of the horizontal bar
    const data = (this.topProducts || []).slice(0, 5);
    const fullNames = data.map((p: any) => p.product_name);
    const labels = fullNames.map((name: string) => this.truncateLabel(name));
    const values = data.map((p: any) => p.total_amount);
    const colors = data.map((_: any, i: number) => this.palette[i % this.palette.length]);

    if (this.topProductsChart) {
      this.topProductsChart.destroy();
    }
    this.topProductsChart = new Chart(this.topProductsChartRef.nativeElement, {
      type: 'horizontalBar',
      data: {
        labels,
        datasets: [
          {
            label: 'Doanh thu',
            data: values,
            backgroundColor: colors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ ticks: { beginAtZero: true, callback: (v: number) => this.formatPrice(v) } }],
        },
        tooltips: {
          callbacks: {
            title: (tooltipItems: any) => fullNames[tooltipItems[0].index],
            label: (tooltipItem: any) => this.formatPrice(Number(tooltipItem.xLabel)) + ' đ',
          },
        },
      },
    });
  }

  formatPrice(price: number): string {
    return (price || 0).toLocaleString('vi-VN');
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return status || '';
    }
  }

  getStatusColor(status: string): string {
    return this.statusColors[status] || '#999';
  }

  getBrandLogo(logo: string): string {
    if (!logo) {
      return '';
    }
    return logo.indexOf('http') === 0 ? logo : this.host + logo;
  }

  getImageUrl(path?: string): string {
    if (!path) {
      return '';
    }
    return path.indexOf('http') === 0 ? path : this.host + path;
  }

  viewAllEquipments() {
    this.router.navigate(['/admin/my-equipment-management']);
  }

  /* Disabled: row click closed in HTML
  viewByBrand(brandId: string) {
    if (!brandId) {
      return;
    }
    this.router.navigate(['/admin/my-equipment-management'], {
      queryParams: { brandId },
    });
  }

  viewByModel(equipmentModelId: string) {
    if (!equipmentModelId) {
      return;
    }
    this.router.navigate(['/admin/my-equipment-management'], {
      queryParams: { equipmentModelId },
    });
  }

  viewByUser(userId: string) {
    if (!userId) {
      return;
    }
    this.router.navigate(['/admin/user-equipment', userId]);
  }
  */
}
